import {
  Box,
  Link,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import React, { FC } from "react";
import { Colors } from "@src/gatsby-theme-material-ui-top-layout/colors-twentyfive";
import { motion } from "motion/react";
import { Button } from "gatsby-theme-material-ui";
import { uiContainerTransitionMs, uiOpacityHover } from "@src/utils";
import { ChevronRight } from "@mui/icons-material";
import { useWebsiteStore } from "@src/hooks/useWebsiteStore";

interface Props {
  navItem: any;
}

const NavItemRoot: FC<Props> = ({ navItem }) => {
  const setMobileExploring = useWebsiteStore(
    (state) => state.actions.setMobileExploring,
  );
  const theme = useTheme();
  const isLarge = useMediaQuery(theme.breakpoints.up("lg"));

  const mobileExploring = useWebsiteStore((state) => state.mobileExploring);

  return (
    <motion.div
      initial={{
        transform: `scale3d(1, 1, 1)`,
      }}
      whileHover={{
        transform: `scale3d(1.01, 1.01, 1)`,
      }}
    >
      <Link
        sx={{
          display: "flex",
          position: "relative",
          borderRadius: "15px",
          border: `solid 1px ${Colors.AiOutline}`,
          fontSize: {
            xs: "12px",
            lg: "15px",
          },
          fontWeight: 400,
          padding: {
            xs: "20px",
            lg: "30px",
          },
          minHeight: {
            xs: "30px",
            lg: "40px",
          },
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          textDecoration: "none",
          width: {
            xs: "300px",
            md: "350px",
            lg: "auto",
          },
          opacity: 1,
          "&:hover": {
            opacity: 1,

            svg: {
              transform: `translate3d(0px, 0px, 0px)`,
              opacity: 1,
            },
          },
          svg: {
            transition: `opacity ${uiContainerTransitionMs}ms ease-in-out, transform ${uiContainerTransitionMs}ms ease-in-out`,
            transform: `translate3d(-10px, 0px, 0px)`,
            opacity: 0,
          },
        }}
        href={isLarge ? `#` : "#"}
        data-cid={navItem.data.categoryId}
        onClick={() => {
          if (!isLarge && mobileExploring) {
            setMobileExploring(false);
          }
        }}
      >
        <Stack
          sx={{
            gap: {
              xs: "16px",
              lg: "20px",
            },
          }}
        >
          <Typography
            variant="h1"
            sx={{
              textAlign: "left",
            }}
          >
            <Box
              component="span"
              sx={{
                display: "block",
                background: "linear-gradient(90deg, #B0E6FF 0%, #05E1F5 30%)",
                backgroundClip: "text",
                textFillColor: "transparent",
                letterSpacing: "3px",
                fontWeight: 500,
                textTransform: "uppercase",
                fontSize: {
                  xs: "16px",
                },
                lineHeight: {
                  xs: "22px",
                },
              }}
            >
              Prompt Injection
            </Box>
            <Box
              component="span"
              sx={{
                display: "flex",
                letterSpacing: 1,
                fontWeight: 600,
                fontSize: {
                  xs: "22px",
                  lg: "24px",
                },
                lineHeight: {
                  xs: "150%",
                },
                alignItems: "center",
              }}
            >
              Interactive Taxonomy{" "}
              <ChevronRight
                sx={{
                  width: "32px",
                  height: "32px",
                  display: {
                    xs: "none",
                    lg: "block",
                  },
                }}
              />
            </Box>
          </Typography>

          <Box
            sx={{
              position: "relative",
              display: "flex",
              width: {
                xs: "95%",
                md: "70%",
                lg: "350px",
              },
              marginX: {
                xs: "auto",
                lg: "initial",
              },
              borderRadius: "15px",
              opacity: 1,
              img: {
                transition: `opacity ${uiContainerTransitionMs}ms  ease-in-out`,
                opacity: 1,
              },
              ":hover": {
                opacity: 1,
              },
            }}
          >
            <Link
              href="https://www.crowdstrike.com/en-us/resources/infographics/taxonomy-of-prompt-injection-methods/"
              target="_blank"
              sx={{
                position: "relative",
                transform: "scale3d(1, 1, 1)",
                transition: `transform ${
                  uiContainerTransitionMs * 2
                }ms ease-in-out, opacity ${uiContainerTransitionMs}ms ease-in-out`,
                "&:hover": {
                  transform: "scale3d(1.02, 1.02, 1.02)",
                },
              }}
            >
              <Box
                component="img"
                src="/taxonomy/poster2.png"
                sx={{
                  backgroundColor: Colors.TextDark,
                  width: "100%",
                  height: "auto",
                  borderRadius: "10px",
                  border: `solid 1px ${Colors.AiOutline}`,
                  marginX: "auto",
                  ":hover": {
                    img: {
                      opacity: uiOpacityHover,
                    },
                  },
                }}
              />

              <Button
                className="poster-hover-button"
                sx={{
                  zIndex: 10,
                  position: "absolute",
                  left: "50%",
                  top: "50%",
                  width: "80%",
                  transform: `translate3d(-50%, -50%, 0)`,
                  "&:hover": {
                    opacity: 1,
                  },
                }}
                variant="contained"
              >
                Download the high-res poster
              </Button>
            </Link>
          </Box>

          <Link
            onClick={() => {
              setMobileExploring(true);

              return false;
            }}
            sx={{
              display: {
                xs: "inline-flex",
                md: "none",
              },
              textDecoration: "none",
            }}
          >
            <Button
              className="explore-taxonomy-button"
              sx={{
                marginX: "auto",
                textDecoration: "none",
                justifyContent: "center",
                alignItems: "center",
                "&:hover": {
                  opacity: 1,
                },
              }}
              variant="contained"
            >
              Explore The Taxonomy
            </Button>
          </Link>

          {/*<Link
            href="https://info.pangea.cloud/prompt-injection-taxonomy-poster?utm_campaign=prompt-injection-taxonomy&utm_source=website&utm_content=taxonomy-intro"
            sx={{
              opacity: 1,
              transition: `opacity ${uiContainerTransitionMs}ms ease-in-out`,
              "&:hover": {
                opacity: `${uiOpacityHover} !important`,
              },
              "&:visited": {
                color: Colors.PrimaryContrast,
              },
              width: "fit-content",
              marginX: "auto",
              textAlign: "center",
              marginTop: {
                xs: "10px",
                lg: 0,
              },
            }}
            target="_blank"
          >
            <Button
              role="button"
              sx={{
                border: `solid 1px ${Colors.AiOutline}`,
                borderRadius: "100px",
                outline: "none",
                display: "inline-flex",
                backgroundColor: "rgba(0, 0, 0, 0.21)",
                boxShadow: "none",
                "&:hover": {
                  opacity: 1,
                },
              }}
            >
              Request a printed poster here
            </Button>
          </Link>*/}
        </Stack>
      </Link>
    </motion.div>
  );
};

export default NavItemRoot;
